import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { CheckCircle2, Circle, Clock, XCircle } from 'lucide-react';
import type { TaskStatus } from '@/types/task';

interface StatusBadgeProps {
  status: TaskStatus;
  onStatusChange?: (newStatus: TaskStatus) => void;
  editable?: boolean;
}

const statusConfig = {
  todo: {
    label: 'A Fazer',
    icon: Circle,
    variant: 'secondary' as const,
    className: 'bg-muted text-muted-foreground hover:bg-muted/80'
  },
  in_progress: {
    label: 'Em Progresso',
    icon: Clock,
    variant: 'default' as const,
    className: 'bg-primary/10 text-primary hover:bg-primary/20'
  },
  completed: {
    label: 'Concluída',
    icon: CheckCircle2,
    variant: 'default' as const,
    className: 'bg-green-500/10 text-green-700 hover:bg-green-500/20 dark:text-green-400'
  },
  cancelled: {
    label: 'Cancelada',
    icon: XCircle,
    variant: 'destructive' as const,
    className: 'bg-destructive/10 text-destructive hover:bg-destructive/20'
  }
};

export const StatusBadge = ({ status, onStatusChange, editable = false }: StatusBadgeProps) => {
  const config = statusConfig[status];
  const Icon = config.icon;

  const badge = (
    <Badge variant={config.variant} className={`${config.className} cursor-pointer`}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </Badge>
  );

  if (!editable || !onStatusChange) {
    return badge;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {badge}
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {Object.entries(statusConfig).map(([key, conf]) => {
          const StatusIcon = conf.icon;
          return (
            <DropdownMenuItem
              key={key}
              onClick={() => onStatusChange(key as TaskStatus)}
              disabled={key === status}
            >
              <StatusIcon className="h-4 w-4 mr-2" />
              {conf.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
